import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ActionButton } from './Forms/ActionButton';
import { useCreateQna, useGetQnaList, useUpdateQna, useDeleteQna } from '../queries/appInfo';
import { FaAngleDown, FaAngleRight, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import ConfirmationModal from './ConfirmationModal';

const Qna = () => {
    const { data: qnaList = [], isPending: isLoadingQnas, refetch } = useGetQnaList();
    const { mutateAsync: createQna, isPending: isCreating } = useCreateQna();
    const { mutateAsync: updateQna, isPending: isUpdating } = useUpdateQna();
    const { mutateAsync: deleteQna, isPending: isDeleting } = useDeleteQna();

    const [showForm, setShowForm] = useState(false);
    const [showQnaList, setShowQnaList] = useState(true);
    const [editingQna, setEditingQna] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [qnaToDelete, setQnaToDelete] = useState(null);

    const {
        handleSubmit,
        formState: { errors },
        reset,
        register,
        setValue
    } = useForm({
        defaultValues: {
            question: '',
            answer: ''
        }
    });

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            if (!data.question.trim() || !data.answer.trim()) {
                toast.error("Please fill in both question and answer");
                return;
            }

            toast.loading(editingQna ? 'Updating Q&A...' : 'Creating Q&A...');

            if (editingQna) {
                await updateQna({
                    id: editingQna._id,
                    question: data.question,
                    answer: data.answer
                });
                toast.dismiss();
                toast.success('Q&A updated successfully!');
                setEditingQna(null);
            } else {
                await createQna({
                    question: data.question,
                    answer: data.answer
                });
                toast.dismiss();
                toast.success('Q&A created successfully!');
            }

            reset();
            setShowForm(false);
            refetch();
        } catch (error) {
            toast.dismiss();
            toast.error(editingQna ? 'Failed to update Q&A' : 'Failed to create Q&A');
            console.error('Error submitting Q&A:', error);
        }
    };

    // Handle edit QnA
    const handleEditQna = (qna) => {
        setEditingQna(qna);
        setValue('question', qna.question || '');
        setValue('answer', qna.answer || '');
        setShowForm(true);
    };

    // Handle delete QnA
    const handleDeleteQna = (qna) => {
        setQnaToDelete(qna);
        setShowDeleteModal(true);
    };

    // Confirm delete QnA
    const confirmDeleteQna = async () => {
        try {
            toast.loading('Deleting Q&A...');
            await deleteQna(qnaToDelete._id);
            toast.dismiss();
            toast.success('Q&A deleted successfully!');
            setShowDeleteModal(false);
            setQnaToDelete(null);
            refetch();
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to delete Q&A');
            console.error('Error deleting Q&A:', error);
        }
    };

    // Cancel form
    const cancelForm = () => {
        reset();
        setEditingQna(null);
        setShowForm(false);
    };

    if (isLoadingQnas) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="loader">Loading Q&A...</div>
            </div>
        );
    }

    return (
        <>
            <div className='bg-white shadow-md rounded-lg p-7'>
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-primary">Q&A Management</h2>
                    <ActionButton
                        text="Add New Q&A"
                        variant="primary"
                        onClick={() => setShowForm(!showForm)}
                        icon={<FaPlus />}
                    />
                </div>

                {/* Add/Edit Form */}
                {showForm && (
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                        <h3 className="text-xl font-semibold mb-4 text-primary">
                            {editingQna ? 'Edit Q&A' : 'Add New Q&A'}
                        </h3>
                        
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="border border-gray-200 rounded-lg p-4 bg-white">
                                {/* Question Input */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Question *
                                    </label>
                                    <input
                                        type="text"
                                        {...register('question', {
                                            required: "Question is required"
                                        })}
                                        placeholder="Enter your question here..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    {errors.question && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.question.message}
                                        </p>
                                    )}
                                </div>

                                {/* Answer Textarea */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Answer *
                                    </label>
                                    <textarea
                                        {...register('answer', {
                                            required: "Answer is required"
                                        })}
                                        placeholder="Enter your answer here..."
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                                    />
                                    {errors.answer && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.answer.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex gap-3 justify-end pt-4">
                                <ActionButton
                                    text="Cancel"
                                    variant="secondary"
                                    onClick={cancelForm}
                                    type="button"
                                />
                                <ActionButton
                                    text={
                                        isCreating || isUpdating
                                            ? editingQna
                                                ? 'Updating...'
                                                : 'Creating...'
                                            : editingQna
                                            ? 'Update Q&A'
                                            : 'Create Q&A'
                                    }
                                    variant="submit"
                                    disabled={isCreating || isUpdating}
                                />
                            </div>
                        </form>
                    </div>
                )}

                {/* Existing Q&A List */}
                <div className="bg-gray-50 rounded-lg">
                    <h3 
                        className="text-xl text-primary font-bold mb-4 cursor-pointer flex items-center p-4" 
                        onClick={() => setShowQnaList(!showQnaList)}
                    >
                        {showQnaList ? <FaAngleDown className='text-secondary mr-2' /> : <FaAngleRight className='text-secondary mr-2' />}
                        Existing Q&A ({qnaList.length})
                    </h3>
                    
                    {showQnaList && (
                        <div className="px-4 pb-4">
                            {qnaList.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p>No Q&A pairs found. Create your first Q&A pair above.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {qnaList?.map((qnaItem, itemIndex) => (
                                        <div key={qnaItem._id} className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-start mb-3">
                                                <h4 className="text-lg font-semibold text-gray-800">
                                                    Q&A #{itemIndex + 1}
                                                </h4>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEditQna(qnaItem)}
                                                        className="text-blue-500 hover:text-blue-700 p-1"
                                                        title="Edit Q&A"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteQna(qnaItem)}
                                                        className="text-red-500 hover:text-red-700 p-1"
                                                        title="Delete Q&A"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="pl-4 border-l-2 border-gray-200">
                                                <div className="mb-3">
                                                    <span className="font-medium text-gray-700">Q: </span>
                                                    <span className="text-gray-800">{qnaItem.question}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-700">A: </span>
                                                    <span className="text-gray-600">{qnaItem.answer}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <ConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={confirmDeleteQna}
                    title="Delete Q&A"
                    message="Are you sure you want to delete this Q&A pair? This action cannot be undone."
                    confirmText="Delete"
                    cancelText="Cancel"
                    isLoading={isDeleting}
                />
            )}

            <ToastContainer
                position="top-right"
                autoHideDuration={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

export default Qna;